import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput, {
  formatLatexPreview,
  parseRootFractionInput,
} from "../../../../components/inputs/RootFractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function formatSubstitutedShift(b) {
  return b < 0 ? `+ ${Math.abs(b)}` : `- ${b}`;
}
function formatValue(value) {
  return Number.isInteger(value)
    ? value.toString()
    : value.toFixed(2).replace(/\.?0+$/, "");
}

function solveIntersectionPoints({ a, b, r, m, c }) {
  const A = 1 + m * m;
  const B = -2 * a + 2 * m * (c - b);
  const C = a * a + (c - b) * (c - b) - r * r;
  const delta = B * B - 4 * A * C;
  const denom = 2 * A;
  const sqrt = simplifySqrt(delta);

  const rawConst = -B;

  const x1 = simplifyFractionWithSqrtProper(
    rawConst,
    -sqrt.outside,
    denom,
    sqrt.inside
  );
  const x2 = simplifyFractionWithSqrtProper(
    rawConst,
    +sqrt.outside,
    denom,
    sqrt.inside
  );

  const y1 = simplifyFractionWithSqrtProper(
    m * x1.numerConst + c * x1.denom,
    m * x1.sqrtCoeff,
    x1.denom,
    x1.sqrtInner
  );
  const y2 = simplifyFractionWithSqrtProper(
    m * x2.numerConst + c * x2.denom,
    m * x2.sqrtCoeff,
    x2.denom,
    x2.sqrtInner
  );

  const point1 = {
    xLatex: formatFractionWithSqrtProper(x1),
    yLatex: formatFractionWithSqrtProper(y1),
    xValue:
      (x1.numerConst + x1.sqrtCoeff * Math.sqrt(x1.sqrtInner)) / x1.denom,
    yValue:
      (y1.numerConst + y1.sqrtCoeff * Math.sqrt(y1.sqrtInner)) / y1.denom,
  };

  const point2 = {
    xLatex: formatFractionWithSqrtProper(x2),
    yLatex: formatFractionWithSqrtProper(y2),
    xValue:
      (x2.numerConst + x2.sqrtCoeff * Math.sqrt(x2.sqrtInner)) / x2.denom,
    yValue:
      (y2.numerConst + y2.sqrtCoeff * Math.sqrt(y2.sqrtInner)) / y2.denom,
  };

  return { A, B, C, delta, sqrt, point1, point2 };
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatCenterPart(varName, val) {
  if (val === 0) return `${varName}^2`;
  return `(${varName} ${val > 0 ? "- " + val : "+ " + Math.abs(val)})^2`;
}

function getCircleEquationLatex(a, b, r) {
  return `${formatCenterPart("x", a)} + ${formatCenterPart("y", b)} = ${
    r ** 2
  }`;
}

function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function simplifyFractionWithSqrtProper(numerConst, sqrtCoeff, denom, sqrtInner) {
  const g = gcd(gcd(Math.abs(numerConst), Math.abs(sqrtCoeff)), Math.abs(denom));
  return {
    numerConst: numerConst / g,
    sqrtCoeff: sqrtCoeff / g,
    denom: denom / g,
    sqrtInner,
  };
}

function formatFractionWithSqrtProper({ numerConst, sqrtCoeff, denom, sqrtInner }) {
  if (!sqrtInner || sqrtInner === 1) {
    const value = numerConst + sqrtCoeff * (sqrtInner === 1 ? 1 : 0);

    if (value % denom === 0) return `${value / denom}`;
    return denom === 1 ? `${value}` : `\\dfrac{${value}}{${denom}}`;
  }

  const parts = [];
  if (numerConst !== 0) parts.push(numerConst.toString());

  if (sqrtCoeff !== 0) {
    const root =
      sqrtCoeff === 1
        ? `\\sqrt{${sqrtInner}}`
        : sqrtCoeff === -1
        ? `-\\sqrt{${sqrtInner}}`
        : `${sqrtCoeff}\\sqrt{${sqrtInner}}`;

    const sign = sqrtCoeff > 0 && numerConst !== 0 ? "+" : "";
    parts.push(`${sign}${root}`);
  }

  if (parts.length === 0) return "0";

  const numerator = parts.join(" ");
  return denom === 1 ? numerator : `\\dfrac{${numerator}}{${denom}}`;
}

function formatSignedNumber(n) {
  return n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`;
}

/**
 * * WYCIĄGNIĘTE POZA renderInput:
 * Dzięki temu typ komponentu Row nie zmienia się przy każdym renderze,
 * co ogranicza remounty/focus-glitche przy RootFractionInput.
 */
function PointsRow({ labelLatex, subIndex, idx, input, setPointField, disabled }) {
  return (
    <div className="space-y-2">
      <div style={{ marginBottom: 6 }}>
        <InlineMath math={labelLatex} />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span>
            <InlineMath math={`x_{${subIndex}} =`} />
          </span>
          <RootFractionInput
            value={input?.[idx]?.x}
            onChange={(val) => setPointField(idx, "x", val)}
            disabled={disabled}
            label={`x_{${subIndex}}`}
          />
        </div>

        <div className="flex items-center gap-2">
          <span>
            <InlineMath math={`y_{${subIndex}} =`} />
          </span>
          <RootFractionInput
            value={input?.[idx]?.y}
            onChange={(val) => setPointField(idx, "y", val)}
            disabled={disabled}
            label={`y_{${subIndex}}`}
          />
        </div>
      </div>
    </div>
  );
}

export function generateTask() {
  let a, b, r, m, c, delta;
  let result;
  let tries = 0;
  let sqrtInside = Infinity;

  do {
    a = getRandomInt(-3, 3);
    b = getRandomInt(-3, 3);
    r = getRandomInt(2, 5);
    m = getRandomInt(-3, 3) || 1;
    c = getRandomInt(-3, 3);

    result = solveIntersectionPoints({ a, b, r, m, c });
    delta = result.delta;
    sqrtInside = result?.sqrt?.inside ?? Infinity;
    tries++;
  } while ((delta <= 0 || sqrtInside > 100) && tries < 200);

  return {
    type: "FindIntersectionPointsTask",
    data: {
      circle: { a, b, r },
      line: { m, c },
      A: result.A,
      B: result.B,
      C: result.C,
      delta: result.delta,
      sqrt: result.sqrt,
      answer: {
        point1: result.point1,
        point2: result.point2,
        approx: [
          [result.point1.xValue, result.point1.yValue],
          [result.point2.xValue, result.point2.yValue],
        ],
      },
    },
  };
}

export function renderTask(task) {
  const data = task.data ?? task;
  const { circle, line } = data;

  const { a, b, r } = circle;
  const { m, c } = line;

  const mStr = m === 1 ? "" : m === -1 ? "-" : m;
  const cStr = c === 0 ? "" : c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`;

  const xExpr =
    a === 0 ? "x^2" : `(x ${a >= 0 ? "- " + a : "+ " + Math.abs(a)})^2`;
  const yExpr =
    b === 0 ? "y^2" : `(y ${b >= 0 ? "- " + b : "+ " + Math.abs(b)})^2`;

  return (
    <div className="space-y-4">
      <div>Dany okrąg i prosta przecinają się. Wyznacz punkty przecięcia:</div>
      <BlockMath math={`${xExpr} + ${yExpr} = ${r ** 2}`} />
      <BlockMath math={`y = ${mStr}x ${cStr}`} />
    </div>
  );
}

/**
 * * INPUTY:
 *  P1: x1, y1 jako RootFractionInput
 *  P2: x2, y2 jako RootFractionInput
 */
export function renderInput(task, userInput, setUserInput, disabled) {
  const input = Array.isArray(userInput) ? userInput : [{}, {}];

  const setPointField = (idx, field, val) => {
    const updated = [...input];
    updated[idx] = { ...(updated[idx] || {}), [field]: val };
    setUserInput(updated);
  };

  return (
    <div className="space-y-4">
      <PointsRow
        labelLatex={"\\mathrm{P}_1"}
        subIndex={"1"}
        idx={0}
        input={input}
        setPointField={setPointField}
        disabled={disabled}
      />
      <PointsRow
        labelLatex={"\\mathrm{P}_2"}
        subIndex={"2"}
        idx={1}
        input={input}
        setPointField={setPointField}
        disabled={disabled}
      />
    </div>
  );
}

export function checkAnswer(task, userInput) {
  const data = task.data ?? task;
  const approxPoints = data?.answer?.approx;

  if (!Array.isArray(approxPoints) || approxPoints.length !== 2) {
    return { isCorrect: false, score: 0, maxScore: 2, parts: { P1: false, P2: false } };
  }

  const epsilon = 0.05;

  const readNumber = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return v;

    if (typeof v === "object") {
      if (typeof v.parsed === "number") return v.parsed;
      const raw = v.raw ?? "";
      const parsed = parseRootFractionInput(raw);
      return typeof parsed === "number" ? parsed : null;
    }

    const parsed = parseRootFractionInput(String(v));
    return typeof parsed === "number" ? parsed : null;
  };

  const extractXY = (pt) => {
    const x = readNumber(pt?.x);
    const y = readNumber(pt?.y);
    return [x, y];
  };

  const closePt = ([x1, y1], [x2, y2]) =>
    typeof x1 === "number" &&
    typeof y1 === "number" &&
    Math.abs(x1 - x2) < epsilon &&
    Math.abs(y1 - y2) < epsilon;

  // user points
  const inArr = Array.isArray(userInput) ? userInput : [];
  const uPts = [extractXY(inArr?.[0]), extractXY(inArr?.[1])];

  // if user didn't provide both coords for a point, treat that point as invalid
  const uValid = uPts.map(([x, y]) => typeof x === "number" && typeof y === "number");

  // expected points
  const [a1, a2] = approxPoints;

  // compute matching as a bipartite "at most once" match
  const matchedExpected = [false, false]; // [a1, a2]
  let score = 0;

  for (let i = 0; i < 2; i++) {
    if (!uValid[i]) continue;

    const u = uPts[i];

    const canMatchA1 = !matchedExpected[0] && closePt(u, a1);
    const canMatchA2 = !matchedExpected[1] && closePt(u, a2);

    if (canMatchA1) {
      matchedExpected[0] = true;
      score += 1;
    } else if (canMatchA2) {
      matchedExpected[1] = true;
      score += 1;
    }
  }

  const parts = { P1: matchedExpected[0], P2: matchedExpected[1] };
  return { isCorrect: score === 2, score, maxScore: 2, parts };
}


export function renderAnswer(task) {
  const data = task.data ?? task;
  const { answer } = data;

  const format = ({ xLatex, yLatex, xValue, yValue }) =>
    `\\left(${xLatex},\\ ${yLatex}\\right) \\approx (${xValue.toFixed(
      2
    )},\\ ${yValue.toFixed(2)})`;

  return (
    <div style={{ textAlign: "left", marginTop: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`\\mathrm{P}_1 = ${format(answer.point1)}`} />
      </div>
      <div>
        <InlineMath math={`\\mathrm{P}_2 = ${format(answer.point2)}`} />
      </div>
    </div>
  );
}

export function renderExplanation(task) {
  const data = task.data ?? task;

  const { A, B, C, delta, circle, line, answer } = data;
  const { a, b, r } = circle;
  const { m, c } = line;

  const { point1, point2 } = answer || {};

  const sqrtDelta = simplifySqrt(delta);
  const deltaLatex =
    sqrtDelta.inside === 1
      ? `${sqrtDelta.outside}`
      : sqrtDelta.outside === 1
      ? `\\sqrt{${sqrtDelta.inside}}`
      : `${sqrtDelta.outside}\\sqrt{${sqrtDelta.inside}}`;

  const denom = 2 * A;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Układ równań i podstawienie do równania kwadratowego:
        <div className="mt-2">Rozpatrujemy układ równań:</div>
        <BlockMath
          math={`\\begin{cases}
      ${getCircleEquationLatex(a, b, r)} \\\\
      y = ${m === 1 ? "" : m === -1 ? "-" : m}x${
            c !== 0 ? ` ${formatSignedNumber(c)}` : ""
          }
    \\end{cases}`}
        />

        <div className="mt-2">
          Podstawiamy za <InlineMath math="y" /> z równania prostej{" "}
          <InlineMath
            math={`y = ${m === 1 ? "" : m === -1 ? "-" : m}x${
              c !== 0 ? ` ${formatSignedNumber(c)}` : ""
            }`}
          />
        </div>

        <BlockMath
          math={`${formatCenterPart("x", a)} + (${
            m === 1 ? "" : m === -1 ? "-" : m
          }x${c !== 0 ? ` ${formatSignedNumber(c)}` : ""}${
            b !== 0 ? ` ${formatSignedNumber(-b)}` : ""
          })^2 = ${r ** 2}`}
        />

        <div className="mt-2">Upraszczamy nawiasy:</div>
        <BlockMath
          math={`${formatCenterPart("x", a)} + (${
            m === 1 ? "" : m === -1 ? "-" : m
          }x${c - b !== 0 ? ` ${formatSignedNumber(c - b)}` : ""})^2 = ${
            r ** 2
          }`}
        />
      </div>

      <div>
        <strong>Krok 2:</strong> Rozwiązujemy równanie kwadratowe:
        <div className="mt-2">Dla trójmianu:</div>
        <BlockMath
          math={`${A}x^2 ${formatSignedNumber(B)}x ${formatSignedNumber(C)} = ${
            r ** 2
          }`}
        />
        <div className="mt-2">Liczymy deltę:</div>
        <BlockMath
          math={`\\Delta = ${
            B < 0 ? `(${B})` : B
          }^2 - 4 \\cdot ${A < 0 ? `(${A})` : A} \\cdot ${
            C < 0 ? `(${C})` : C
          } = ${delta}`}
        />
        <div className="mt-2">Wyznaczamy:</div>
        <BlockMath
          math={`x_1 = \\frac{-(${B}) - ${deltaLatex}}{${denom}} = ${
            point1.xLatex
          }`}
        />
        <BlockMath
          math={`x_2 = \\frac{-(${B}) + ${deltaLatex}}{${denom}} = ${
            point2.xLatex
          }`}
        />

        <div className="mt-3">Podstawiamy do prostej:</div>
        <BlockMath
          math={`y = ${m === 1 ? "" : m === -1 ? "-" : m}x${
            c !== 0 ? ` ${formatSignedNumber(c)}` : ""
          }`}
        />
        <BlockMath
          math={`y_1 = ${
            m === 1 ? "" : m === -1 ? "-" : `${m} \\cdot `
          }\\left(${point1.xLatex}\\right)${
            c !== 0 ? ` ${formatSignedNumber(c)}` : ""
          } = ${point1.yLatex}`}
        />
        <BlockMath
          math={`y_2 = ${
            m === 1 ? "" : m === -1 ? "-" : `${m} \\cdot `
          }\\left(${point2.xLatex}\\right)${
            c !== 0 ? ` ${formatSignedNumber(c)}` : ""
          } = ${point2.yLatex}`}
        />
      </div>

      {point1 && point2 && (
        <>
          <div>
            <strong>Krok 3:</strong> Odpowiedź:
            <div style={{ marginLeft: "1rem", marginTop: "0.5rem" }}>
              <InlineMath
                math={`\\mathrm{P}_1 = \\left(${point1.xLatex},\\ ${
                  point1.yLatex
                }\\right) \\approx\\ (${formatValue(point1.xValue)},\\ ${formatValue(
                  point1.yValue
                )})`}
              />
            </div>
            <div style={{ marginLeft: "1rem", marginTop: "0.3rem" }}>
              <InlineMath
                math={`\\mathrm{P}_2 = \\left(${point2.xLatex},\\ ${
                  point2.yLatex
                }\\right) \\approx\\ (${formatValue(point2.xValue)},\\ ${formatValue(
                  point2.yValue
                )})`}
              />
            </div>
          </div>

        <div style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
{(() => {
  const pad = 2;

  // okrąg
  const baseXmin = a - r - pad;
  const baseXmax = a + r + pad;
  const baseYmin = b - r - pad;
  const baseYmax = b + r + pad;

  // punkty (jeśli masz point1/point2)
  const xs = [baseXmin, baseXmax, point1.xValue, point2.xValue].filter(
    (v) => typeof v === "number" && !Number.isNaN(v)
  );
  const ys = [baseYmin, baseYmax, point1.yValue, point2.yValue].filter(
    (v) => typeof v === "number" && !Number.isNaN(v)
  );

  const xmin = Math.min(...xs);
  const xmax = Math.max(...xs);
  const ymin = Math.min(...ys);
  const ymax = Math.max(...ys);

  return (
    <GraphViewer
      circles={[
        { center: { x: a, y: b }, radius: r, label: "O" }
      ]}
      points={{
        P1: { x: point1.xValue, y: point1.yValue, label: "P1" },
        P2: { x: point2.xValue, y: point2.yValue, label: "P2" },
      }}
      equations={[
        {
          expr: `y = ${m}*x + (${c})`,
          color: "#00c466",
          label: "prosta",
        },
      ]}
      xRange={[xmin, xmax]}
      yRange={[ymin, ymax]}
      showGrid
      width={500}
      height={500}
    />
  );
})()}

        </div>

        </>
      )}
    </div>
  );
}
export function renderUserAnswer(userInput) {
  const input = Array.isArray(userInput) ? userInput : [{}, {}];

  const hasValue = (v) => {
    if (v == null) return false;
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "object") {
      if (typeof v.raw === "string") return v.raw.trim() !== "";
      if (typeof v.parsed === "number") return Number.isFinite(v.parsed);
    }
    return false;
  };

  const hasAny =
    hasValue(input?.[0]?.x) ||
    hasValue(input?.[0]?.y) ||
    hasValue(input?.[1]?.x) ||
    hasValue(input?.[1]?.y);

  if (!hasAny) return <i>(brak odpowiedzi)</i>;

  const fmt = (v) => {
    if (v == null) return "\\text{—}";
    if (typeof v === "number") return String(v);
    if (typeof v === "object") {
      const raw = v.raw ?? "";
      const latex = formatLatexPreview(raw);
      return latex || "\\text{—}";
    }
    const latex = formatLatexPreview(String(v));
    return latex || "\\text{—}";
  };

  const x1 = fmt(input?.[0]?.x);
  const y1 = fmt(input?.[0]?.y);
  const x2 = fmt(input?.[1]?.x);
  const y2 = fmt(input?.[1]?.y);

  return (
    <div style={{ textAlign: "left", marginTop: "1rem" }}>
      <div style={{ marginBottom: "0.6rem" }}>
        <InlineMath math={`\\mathrm{P}_1 = \\left(${x1},\\ ${y1}\\right)`} />
      </div>
      <div>
        <InlineMath math={`\\mathrm{P}_2 = \\left(${x2},\\ ${y2}\\right)`} />
      </div>
    </div>
  );
}
